import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import i18n from 'i18next';
import {
  AgentTierOption,
  getAgentTierOptions,
} from '@/application/requests/agentTier';

export interface AgentTierState {
  allAgentTiers: AgentTierOption[];
  allAgentTiersLoading: boolean;
  allAgentTiersLoaded: boolean;
  loadedLanguage: string | null;
  allAgentTiersWithDeleted: AgentTierOption[];
  allAgentTiersWithDeletedLoading: boolean;
  allAgentTiersWithDeletedLoaded: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const initialState: AgentTierState = {
  allAgentTiers: [],
  allAgentTiersLoading: false,
  allAgentTiersLoaded: false,
  loadedLanguage: null,
  allAgentTiersWithDeleted: [],
  allAgentTiersWithDeletedLoading: false,
  allAgentTiersWithDeletedLoaded: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
};

export const fetchAllAgentTiersForCombobox = createAsyncThunk(
  'agentTier/fetchAllAgentTiersForCombobox',
  async (_, { rejectWithValue }) => {
    try {
      const currentLanguage = i18n.language || 'en';
      const response = await getAgentTierOptions();

      if (!response.success || !response.data) {
        return rejectWithValue(
          response.message || 'Failed to fetch all agent tiers for combobox'
        );
      }

      return {
        data: response.data,
        language: currentLanguage,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch all agent tiers for combobox'
      );
    }
  }
);

export const fetchAllAgentTiersForComboboxHandlers = {
  pending: (state: any) => {
    state.allAgentTiersLoading = true;
  },
  fulfilled: (
    state: any,
    action: PayloadAction<{ data: AgentTierOption[]; language: string }>
  ) => {
    state.allAgentTiersLoading = false;
    state.allAgentTiersLoaded = true;
    state.allAgentTiers = action.payload.data;
    state.loadedLanguage = action.payload.language;
  },
  rejected: (state: any, action: any) => {
    state.allAgentTiersLoading = false;
    state.allAgentTiersLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
      state.allAgentTiers = [];
    }
  },
};

export const fetchAllAgentTiersForComboboxWithDeleted = createAsyncThunk(
  'agentTier/fetchAllAgentTiersForComboboxWithDeleted',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAgentTierOptions({ includeDeleted: true });
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.message || 'Failed to fetch agent tier options'
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch agent tier options'
      );
    }
  }
);

export const fetchAllAgentTiersForComboboxWithDeletedHandlers = {
  pending: (state: any) => {
    state.allAgentTiersWithDeletedLoading = true;
  },
  fulfilled: (state: any, action: PayloadAction<AgentTierOption[]>) => {
    state.allAgentTiersWithDeletedLoading = false;
    state.allAgentTiersWithDeletedLoaded = true;
    state.allAgentTiersWithDeleted = action.payload;
  },
  rejected: (state: any, action: any) => {
    state.allAgentTiersWithDeletedLoading = false;
    state.allAgentTiersWithDeletedLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
      state.allAgentTiersWithDeleted = [];
    }
  },
};

const agentTierSlice = createSlice({
  name: 'agentTier',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAllAgentTiersForCombobox.pending,
        fetchAllAgentTiersForComboboxHandlers.pending
      )
      .addCase(
        fetchAllAgentTiersForCombobox.fulfilled,
        fetchAllAgentTiersForComboboxHandlers.fulfilled
      )
      .addCase(
        fetchAllAgentTiersForCombobox.rejected,
        fetchAllAgentTiersForComboboxHandlers.rejected
      )
      .addCase(
        fetchAllAgentTiersForComboboxWithDeleted.pending,
        fetchAllAgentTiersForComboboxWithDeletedHandlers.pending
      )
      .addCase(
        fetchAllAgentTiersForComboboxWithDeleted.fulfilled,
        fetchAllAgentTiersForComboboxWithDeletedHandlers.fulfilled
      )
      .addCase(
        fetchAllAgentTiersForComboboxWithDeleted.rejected,
        fetchAllAgentTiersForComboboxWithDeletedHandlers.rejected
      );
  },
});

export const { setCurrentPage } = agentTierSlice.actions;
export default agentTierSlice.reducer;
